
import ReactDOMServer from 'react-dom/server';
import ConsentFormPdfTemplate from '../components/ConsentFormPdfTemplate.tsx';
import html2pdf from 'html2pdf.js';
import React, { useState, useEffect, useRef } from 'react';
import { getSurgicalTemplates } from '../contexts/constants';
import { useLanguage } from '../contexts/LanguageContext';
import { usePatient } from '../contexts/PatientContext';
import { generateSurgicalNote, generatePatientOrientations } from '../services/geminiService';
import { ClinicalNote, Doctor, createInitialClinicalRegistry } from '../contexts/types';
import { PremiumActionButton, PremiumEmptyState, PremiumGrid, PremiumInput, PremiumModuleCard, PremiumPageShell, PremiumSectionCard } from './premium';
import {
  Sparkles, 
  Copy, 
  Download, 
  FileText, 
  CheckCircle2, 
  RotateCcw, 
  Mic, 
  MicOff, 
  Save, 
  Bookmark, 
  UserCheck, 
  FileSignature,
  Trash2,
  FilePlus,
  Check,
  ClipboardList,
  FileText as FileTextIcon // Alias for consent form icon
} from 'lucide-react';

// --- RICH TEXT RENDERER ---
const parseBold = (text: string) => {
  return text.split(/(\*\*.*?\*\*)/g).map((part, j) => 
    part.startsWith('**') && part.endsWith('**') 
      ? <strong key={j} className="text-white font-bold">{part.slice(2, -2)}</strong> 
      : part
  );
};

const renderMarkdown = (text: string) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Headers
    if (line.startsWith('## ')) return <h3 key={i} className="text-lg font-bold text-blue-300 mt-4 mb-2 border-b border-blue-500/30 pb-1">{line.replace('## ', '')}</h3>;
    if (line.startsWith('### ')) return <h4 key={i} className="text-md font-bold text-purple-300 mt-3 mb-1">{line.replace('### ', '')}</h4>;
    if (line.startsWith('# ')) return <h2 key={i} className="text-xl font-bold text-white mt-5 mb-3">{line.replace('# ', '')}</h2>;
    
    const trimmed = line.trim();
    // Lists
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
       return (
         <div key={i} className="flex gap-2 mb-1 ml-1">
            <span className="text-blue-500 mt-2 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 block"></span>
            <span className="text-slate-300 text-sm leading-relaxed">{parseBold(trimmed.replace(/^[*•-]\s*/, ''))}</span>
         </div>
       );
    }

    // Numbered Lists (1. 2. etc)
    if (/^\d+\.\s/.test(trimmed)) {
       return (
         <div key={i} className="flex gap-2 mb-1 ml-1">
            <span className="text-blue-400 font-bold text-sm mt-0.5 shrink-0">{trimmed.match(/^\d+\./)?.[0]}</span>
            <span className="text-slate-300 text-sm leading-relaxed">{parseBold(trimmed.replace(/^\d+\.\s*/, ''))}</span>
         </div>
       );
    }

    // Empty lines
    if (!trimmed) return <div key={i} className="h-2" />;

    // Paragraphs
    return <p key={i} className="text-slate-300 text-sm leading-relaxed mb-1">{parseBold(line)}</p>;
  });
};

interface CustomTemplate {
  id: string;
  procedureId: string;
  name: string;
  content: string;
}

const BureaucracyModule: React.FC = () => {
  const { language, t } = useLanguage();
  const { activePatient, updatePatient } = usePatient();
  const currentLocale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR';
  const templates = getSurgicalTemplates(language);
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0].id);
  const [extraDetails, setExtraDetails] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [outputType, setOutputType] = useState<'technical' | 'patient' | 'consent' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedToPatient, setSavedToPatient] = useState(false);
  const [consentFormContent, setConsentFormContent] = useState('');
  const [consentFormData, setConsentFormData] = useState<any>(null); // Structured data for the consent form
  
  // Doctor Data for PDF
  const [doctorData, setDoctorData] = useState<Doctor | null>(null);

  useEffect(() => {
    const users = JSON.parse(localStorage.getItem('orthoprecision_users') || '[]');
    const current = users.find((u: any) => u.isLoggedIn);
    if (current) {
      setDoctorData({ name: current.name, crm: current.crm, rqe: current.rqe });
    }
  }, []);
  // Voice State
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Template State
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');

  // Reset saved status on new generation
  useEffect(() => {
    setSavedToPatient(false);
  }, [generatedText, consentFormContent]);

  // Load templates on mount
  useEffect(() => {
    const saved = localStorage.getItem('orthoprecision_doc_templates');
    if (saved) {
      try {
        setCustomTemplates(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load templates", e);
      }
    }
  }, []);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = language === 'pt' ? 'pt-BR' : (language === 'es' ? 'es-ES' : 'en-US');

      recognitionRef.current.onresult = (event: any) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }
        
        // Append only final results to state to avoid duplication in loop
        if (finalTranscript) {
             setExtraDetails(prev => {
                 const spacing = prev.length > 0 && !prev.endsWith(' ') ? ' ' : '';
                 return prev + spacing + finalTranscript;
             });
        }
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error("Speech recognition error", event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [language]);

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.start();
        setIsListening(true);
      } else {
        alert(t('bureaucracySpeechNotSupported'));
      }
    }
  };

  const handleSaveTemplate = () => {
    if (!newTemplateName.trim() || !extraDetails.trim()) return;
    
    const newTemplate: CustomTemplate = {
      id: Date.now().toString(),
      procedureId: selectedTemplate,
      name: newTemplateName,
      content: extraDetails
    };

    const updated = [...customTemplates, newTemplate];
    setCustomTemplates(updated);
    localStorage.setItem('orthoprecision_doc_templates', JSON.stringify(updated));
    
    setNewTemplateName('');
    setShowSaveTemplate(false);
  };

  const handleDeleteTemplate = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = customTemplates.filter(t => t.id !== id);
    setCustomTemplates(updated);
    localStorage.setItem('orthoprecision_doc_templates', JSON.stringify(updated));
  }

  const handleLoadTemplate = (content: string) => {
    setExtraDetails(content);
  };

  const handleSynthesizeFromNotes = () => {
    if (!activePatient) return;
    
    const relevantNotes = (activePatient.clinicalNotes ?? []).filter(n => 
      n.type === 'Atlas' || n.type === 'Protocol' || n.type === 'Consult'
    );

    if (relevantNotes.length === 0) {
      alert(t("noTechnicalNoteFound"));
      return;
    }

    // Pull the most recent Atlas and Protocol notes
    const atlasNote = relevantNotes.find(n => n.type === 'Atlas');
    const protocolNote = relevantNotes.find(n => n.type === 'Protocol' || n.type === 'Consult');

    let synthesizedText = "";
    if (atlasNote) {
      synthesizedText += `[${t('bureaucracyAtlasDataHeader')}]:\n${atlasNote.content}\n\n`;
    }
    if (protocolNote) {
      synthesizedText += `[${t('bureaucracyProtocolDataHeader')}]:\n${protocolNote.content}\n\n`;
    }

    if (synthesizedText) {
      setExtraDetails(prev => prev + (prev ? '\n\n' : '') + synthesizedText);
    }
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setOutputType('technical');
    const template = templates.find(t => t.id === selectedTemplate) || templates[0];
    
    const context = `${t('bureaucracyTechnicalDetailsContext')}: ${extraDetails}`;

    const result = await generateSurgicalNote(template.title, context, language);
    setGeneratedText(result || '');
    setIsLoading(false);
  };

  const handleGenerateOrientations = async () => {
    setIsLoading(true);
    setOutputType('patient');
    const template = templates.find(t => t.id === selectedTemplate) || templates[0];
    
    const procedureContext = template.title;

    const result = await generatePatientOrientations(procedureContext, language);
    setGeneratedText(result || '');
    setIsLoading(false);
  };

  const generateConsentForm = () => {
    if (!activePatient) {
      alert(t("selectPatientFirst"));
      return;
    }

    setIsLoading(true);
    setOutputType('consent');
    setGeneratedText(''); // Clear other generated text

    const patientName = activePatient.name || t('bureaucracyPatientUndefined');
    const patientCpf = activePatient.cpf || t('bureaucracyNotInformed');
    const patientAge = activePatient.age ? t('yearsOld', { count: activePatient.age }) : t('bureaucracyNotInformedFemale');
    const patientSex = activePatient.sex === 'M' ? t('male') : t('female');
    const patientComorbidities = activePatient.comorbidities?.join(', ') || t('bureaucracyNone');
    const patientAllergies = activePatient.allergies?.join(', ') || t('bureaucracyNone');
    
    // Try to find diagnosis and surgical plan from clinical notes if empty
    let patientDiagnosis = activePatient.diagnosis;
    let patientSurgicalPlan = activePatient.surgicalPlan;

    if (!patientDiagnosis || !patientSurgicalPlan) {
      const relevantNotes = activePatient.clinicalNotes || [];
      
      // Search for diagnosis in notes
      if (!patientDiagnosis) {
        const diagNote = relevantNotes.find(
          (n) => /diagnóstico|diagnostico|diagnosis/i.test(n.content) || n.type === 'Atlas'
        );
        if (diagNote) {
          patientDiagnosis = diagNote.content
            .split('\n')[0]
            .replace(/(?:Diagnóstico|Diagnostico|Diagnosis):?\s*/i, '');
        }
      }

      // Search for surgical plan in notes
      if (!patientSurgicalPlan) {
        const planNote = relevantNotes.find(n => n.content.toLowerCase().includes('conduta') || n.content.toLowerCase().includes('cirurgia') || n.type === 'Protocol');
        if (planNote) {
          patientSurgicalPlan = planNote.content.split('\n')[0].replace(/Conduta:?\s*/i, '');
        }
      }
    }

    patientDiagnosis = patientDiagnosis || t('bureaucracyNotInformed');
    patientSurgicalPlan = patientSurgicalPlan || t('bureaucracyNotInformed');

    // Doctor data priority: Patient's responsible doctor > Logged in doctor
    const doc = activePatient.responsibleDoctor || doctorData;
    const doctorName = doc?.name || t('bureaucracyDoctorPlaceholder');
    const doctorCrm = doc?.crm || '[CRM]';
    const doctorRqe = doc?.rqe ? `RQE ${doc.rqe}` : '';

    const date = new Date();
    const formattedDate = date.toLocaleDateString(currentLocale, { day: '2-digit', month: 'long', year: 'numeric' });
    const location = t('bureaucracyDefaultLocation');

    // Set structured data for the new template
    setConsentFormData({
      patient: activePatient,
      doctor: { name: doctorName, crm: doctorCrm, rqe: doc?.rqe },
      diagnosis: patientDiagnosis,
      surgicalPlan: patientSurgicalPlan,
      location: location,
      formattedDate: formattedDate,
    });

    // For backward compatibility or simple text display, keep the text content as well
    const consentText = `
${t('consentFormHeading')}

${t('consentFormIntro', { patientName, patientCpf, patientAge, patientSex, surgicalPlan: patientSurgicalPlan })}

${t('consentFormRisks', { patientAllergies })}

${t('consentFormHealthConditions', { patientComorbidities })}
${t('consentFormMainDiagnosis', { patientDiagnosis })}

${t('consentFormNoGuarantee')}

${t('consentFormAuthorization', { doctorName, doctorCrm, doctorRqe })}

${t('consentFormLocationDate', { location, formattedDate })}

____________________________________________________
${t('consentFormPatientSignature')}

____________________________________________________
${t('consentFormDoctorSignature')}
${doctorName} - CRM ${doctorCrm} ${doctorRqe}
${t('patientTechnicalResponsibility')}
    `;
    setConsentFormContent(consentText);
    setIsLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveToPatient = () => {
    if (!activePatient) {
        alert(t("selectPatientFirst"));
        return;
    }
    
    const template = templates.find(t => t.id === selectedTemplate) || templates[0];
    const docTitle = outputType === 'technical' 
        ? `${t('bureaucracyTechnicalNotePrefix')}: ${template.title}` 
        : outputType === 'patient' 
        ? `${t('bureaucracyPatientGuidancePrefix')}: ${template.title}`
        : `${t('bureaucracyConsentFormPrefix')}: ${activePatient.name}`;

    const contentToSave = outputType === 'consent' ? consentFormContent : generatedText;

    const newNote: ClinicalNote = {
        id: Date.now().toString(),
        date: Date.now(),
        type: outputType === 'consent' ? 'Consent' : 'Document',
        title: docTitle,
        content: contentToSave
    };

    const existingRegistry = activePatient.clinicalRegistry ?? createInitialClinicalRegistry();
    const procedureDate = new Date().toISOString().split('T')[0];
    const shouldRefreshProcedureDate =
      outputType === 'consent' || !existingRegistry.procedure.procedureDate;

    updatePatient(activePatient.id, {
        clinicalNotes: [newNote, ...(activePatient.clinicalNotes || [])],
        clinicalRegistry: {
          ...existingRegistry,
          procedure: {
            ...existingRegistry.procedure,
            procedureType: template.title || existingRegistry.procedure.procedureType,
            procedureDate: shouldRefreshProcedureDate
              ? procedureDate
              : existingRegistry.procedure.procedureDate,
          },
        },
    });
    setSavedToPatient(true);
    setTimeout(() => setSavedToPatient(false), 3000);
  };

  const handleExportPDF = () => {
    const contentToExport = outputType === 'consent' ? consentFormContent : generatedText;
    if (!contentToExport) return;

    const patientName = activePatient?.name || 'document';
    const docType = outputType || 'general';
    const filename = `${docType}_${patientName.replace(/ /g, '_')}.pdf`;

    const element = document.getElementById('consent-form-pdf-content');
    if (!element) {
      console.error(t('bureaucracyPdfTargetMissing'));
      return;
    }

    const opt = {
      margin:       0.5,
      filename:     filename,
      image:        { type: 'jpeg' as 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' as 'portrait' }
    };

    html2pdf().from(element).set(opt).save();
  };

  const filteredTemplates = customTemplates.filter(t => t.procedureId === selectedTemplate);

  return (
    <PremiumPageShell className="space-y-6 animate-in fade-in duration-500">
      <PremiumSectionCard
        title={t('bureaucracyTitle')}
        subtitle={t('bureaucracySubtitle')}
        className="p-6"
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: Configuration */}
        <div className="xl:col-span-1 space-y-4">
          <div className="glass p-6 rounded-2xl space-y-6 shadow-xl">
            
            {/* Procedures List */}
            <div className="space-y-4">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                <FileSignature className="w-4 h-4" /> {t('bureaucracyProcedureBase')}
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar pr-1">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTemplate(t.id)}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      selectedTemplate === t.id 
                        ? 'bg-cyan-500/10 border-[color:var(--op-border-strong)] text-white shadow-lg shadow-cyan-500/10' 
                        : 'bg-[color:var(--op-card-bg-soft)] border-[color:var(--op-border)] text-[color:var(--op-text-muted)] hover:border-[color:var(--op-border-strong)] hover:text-[color:var(--op-text)]'
                    }`}
                  >
                    <div className="text-sm font-semibold text-[color:var(--op-text)]">{t.title}</div>
                    <div className="mt-0.5 truncate text-[10px] opacity-70">{t.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Smart Details Input */}
            <div className="space-y-3 relative">
              <div className="flex justify-between items-end">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  {t("specificDetails")}
                </label>
                <div className="flex gap-2">
                   <button 
                     onClick={handleSynthesizeFromNotes}
                    className="text-[10px] bg-cyan-500/10 px-2 py-1 rounded text-cyan-300 border border-cyan-400/20 hover:bg-cyan-500/20 flex items-center gap-1 transition-all"
                    title={t("synthesizeFromRecord")}
                  >
                    <ClipboardList className="w-3 h-3" /> {t("synthesizeFromRecord")}
                   </button>
                   {/* Template Toggle */}
                   {filteredTemplates.length > 0 && (
                     <div className="relative group">
                        <button className="text-[10px] bg-[color:var(--op-card-bg-soft)] px-2 py-1 rounded text-[color:var(--op-accent)] border border-[color:var(--op-border)] hover:bg-cyan-500/10 flex items-center gap-1 transition-all">
                           <Bookmark className="w-3 h-3" /> {t('myTemplates')} ({filteredTemplates.length})
                        </button>
                        {/* Dropdown for Templates */}
                        <div className="absolute right-0 bottom-full z-20 mb-2 hidden w-48 rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)] p-1 shadow-xl group-hover:block">
                           {filteredTemplates.map(ft => (
                             <div key={ft.id} className="flex items-center justify-between rounded-lg p-2 hover:bg-white/5 group/item">
                               <button 
                                 onClick={() => handleLoadTemplate(ft.content)}
                                 className="flex-1 truncate text-left text-xs text-[color:var(--op-text-muted)] hover:text-[color:var(--op-text)]"
                               >
                                 {ft.name}
                               </button>
                               <button onClick={(e) => handleDeleteTemplate(ft.id, e)} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3"/></button>
                             </div>
                           ))}
                        </div>
                     </div>
                   )}
                </div>
              </div>
              
              <div className="relative">
                <textarea
                  value={extraDetails}
                  onChange={(e) => setExtraDetails(e.target.value)}
                  placeholder={t('detailsPlaceholder')}
                  className={`w-full rounded-xl p-4 text-sm outline-none transition-all h-40 resize-none op-input ${isListening ? 'border-red-500/50 ring-1 ring-red-500/20' : ''}`}
                />
                
                {/* Voice & Save Controls */}
                <div className="absolute bottom-3 right-3 flex gap-2">
                  <button 
                    onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                    className="p-2 bg-[color:var(--op-card-bg-soft)] hover:bg-cyan-500/10 text-[color:var(--op-text-muted)] hover:text-[color:var(--op-accent)] rounded-lg backdrop-blur-sm transition-all"
                    title={t('saveTemplate')}
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={toggleListening}
                    className={`p-2 rounded-lg backdrop-blur-sm transition-all flex items-center gap-2 ${
                      isListening 
                      ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/20' 
                      : 'bg-[color:var(--op-card-bg-soft)] hover:bg-cyan-500/10 text-[color:var(--op-text-muted)] hover:text-[color:var(--op-accent)]'
                    }`}
                    title={t("voiceDictation")}
                  >
                    {isListening ? (
                       <><Mic className="w-4 h-4" /><span className="text-[10px] font-bold">{t('listening')}</span></>
                    ) : (
                       <MicOff className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Save Template Input */}
              {showSaveTemplate && (
                 <div className="flex gap-2 animate-in slide-in-from-top-2">
                    <input 
                      type="text" 
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      placeholder={t("templateNamePlaceholder")}
                      className="flex-1 op-input rounded-lg px-3 text-xs"
                    />
                    <button 
                      onClick={handleSaveTemplate}
                      className="px-3 py-2 bg-gradient-to-r from-cyan-500/20 to-sky-500/20 text-white rounded-lg text-xs font-bold transition-colors border border-[color:var(--op-border)]"
                    >
                      OK
                    </button>
                 </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={handleGenerate}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-blue-900/20 group"
              >
                {isLoading && outputType === 'technical' ? <RotateCcw className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4 group-hover:scale-110 transition-transform" />}
                <div className="flex flex-col items-start leading-tight">
                   <span className="text-xs">{t('generateDesc')}</span>
                </div>
              </button>
              <button
                onClick={handleGenerateOrientations}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-50 border border-slate-600 hover:border-slate-500 group"
              >
                {isLoading && outputType === 'patient' ? <RotateCcw className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4 group-hover:scale-110 transition-transform" />}
                <div className="flex flex-col items-start leading-tight">
                   <span className="text-xs">{t('generateOrient')}</span>
                </div>
              </button>
              <button
                onClick={generateConsentForm}
                disabled={!activePatient || isLoading}
                className="col-span-2 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-purple-900/20 group"
              >
                <FileText className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <div className="flex flex-col items-start leading-tight">
                   <span className="text-xs">{t("generateConsentForm")}</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Right: Output */}
        <div className="xl:col-span-2">
            <div className="glass h-full rounded-2xl flex flex-col overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-[color:var(--op-border)] flex justify-between items-center bg-[color:var(--op-card-bg-soft)]">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${outputType === 'patient' ? 'bg-emerald-500/20 text-emerald-400' : outputType === 'consent' ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-500/20 text-cyan-300'}`}>
                   {outputType === 'patient' ? <UserCheck className="w-5 h-5" /> : outputType === 'consent' ? <FileTextIcon className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                </div>
                <div>
                   <h3 className="text-sm font-bold text-white">
                      {outputType === 'patient' ? t('patientGuide') : outputType === 'consent' ? t('consentForm') : t('medicalRecordNote')}
                   </h3>
                   <p className="text-[10px] uppercase tracking-widest text-[color:var(--op-text-muted)]">
                      {outputType === 'patient' ? t('simplifiedLanguage') : outputType === 'consent' ? t('patientData') : t('technicalTerminology')}
                   </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                {activePatient && (generatedText || consentFormContent) && (
                    <button 
                      onClick={handleSaveToPatient}
                      className={`p-2 rounded-lg transition-colors border flex items-center gap-2 ${savedToPatient ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' : 'hover:bg-cyan-500/10 text-[color:var(--op-text-muted)] hover:text-[color:var(--op-text)] border-transparent hover:border-[color:var(--op-border)]'}`}
                      title={t("saveToRecord")}
                    >
                      {savedToPatient ? <Check className="w-5 h-5" /> : <FilePlus className="w-5 h-5" />}
                    </button>
                )}
                
                <div className="w-px h-6 bg-[color:var(--op-border)] mx-1" />

                <button 
                  onClick={handleCopy}
                  className="relative p-2 rounded-lg border border-transparent text-[color:var(--op-text-muted)] transition-colors hover:bg-cyan-500/10 hover:text-[color:var(--op-text)] hover:border-[color:var(--op-border)]"
                  title={t("copyText")}
                >
                  {copied ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
                <button onClick={handleExportPDF} className="p-2 hover:bg-cyan-500/10 rounded-lg text-[color:var(--op-text-muted)] hover:text-[color:var(--op-text)] transition-colors border border-transparent hover:border-[color:var(--op-border)]" title={t("exportPdf")}>
                  <Download className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 p-8 overflow-y-auto bg-[color:var(--op-bg-deep)]/30">
              {isLoading ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-[color:var(--op-text-muted)] animate-pulse">
                  <Sparkles className="h-10 w-10 animate-bounce text-cyan-300/50" />
                  <p className="text-xs font-bold uppercase tracking-widest">{t('loading')}</p>
                </div>
              ) : generatedText ? (
                <div className="prose prose-invert prose-sm max-w-none">
                   {/* UPDATED: Use Markdown Renderer */}
                   <div className="font-sans text-slate-300 leading-relaxed text-sm">
                      {renderMarkdown(generatedText)}
                   </div>
                </div>
              ) : consentFormData ? (
                <div id="consent-form-pdf-content" className="prose prose-invert prose-sm max-w-none bg-white text-gray-800 p-8 rounded-lg shadow-lg">
                  <ConsentFormPdfTemplate
                    patient={consentFormData.patient}
                    doctor={consentFormData.doctor}
                    diagnosis={consentFormData.diagnosis}
                    surgicalPlan={consentFormData.surgicalPlan}
                    location={consentFormData.location}
                    formattedDate={consentFormData.formattedDate}
                  />
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-4 opacity-50 text-[color:var(--op-text-muted)]">
                  <FileText className="h-16 w-16" />
                  <p className="text-xs font-bold uppercase tracking-widest">{t("waitingGeneration")}</p>
                </div>
              )}
            </div>
            
            {/* Footer Status */}
            <div className="flex items-center justify-between border-t border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)] px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-[color:var(--op-text-muted)]">
               <span>{t('bureaucracyAiEngineVersion')}</span>
               <span>{generatedText ? t('charactersCount', { count: generatedText.length }) : t('ready')}</span>
            </div>
          </div>
        </div>
      </div>
    </PremiumPageShell>
  );
};

export default BureaucracyModule;
